/** A value that is missing or unknown, wherever a value would be shown. */
export const MISSING_VALUE = '—';

export function stringToUpperCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function thousandSeparator(value: string, separator = ' ') {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

export const trimAllWhiteCharacters = (mobileNumber: string) => mobileNumber.replace(/\s+/g, '');

export const pluralize = (value: number, word: string, plural?: string) => {
  return value === 1 ? word : (plural ?? `${word}s`);
};

export const pluralizeWithValue = ({ value, word }: { value: number; word: string }) => {
  if (value === 1) {
    return `${value} ${word}`;
  }
  return `${value} ${pluralize(value, word)}`;
};

/**
 * First and last name's initials, so a middle name never decides them: "Tom Arne Hansen" is "TH"
 * wherever he shows, never "TA" beside Tuan Anh Vu. A one-word name is its first letter.
 */
export const extractUserInitialsFromName = (name: string) => {
  const words = name.split(' ').filter((part) => part.length > 0);
  return [words[0], words.length > 1 ? words.at(-1) : undefined]
    .map((word) => word?.charAt(0) ?? '')
    .join('')
    .toUpperCase();
};

/**
 * Transforms constant-case or snake_case strings to sentence case
 * Examples:
 * - ASSIGNED_TO_ROUTE -> Assigned to route
 * - stone_chip_repair -> Stone chip repair
 * - CONFIRMED -> Confirmed
 * @param value - The constant-case or snake_case string to transform
 * @returns The transformed string in sentence case
 */
export const formatConstantCaseToSentenceCase = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase());
};

// Common acronyms that should remain uppercase
const ACRONYMS = ['SMS', 'API', 'ID', 'URL', 'HTTP', 'HTTPS', 'HTML', 'CSS', 'JSON', 'XML', 'PDF', 'CSV'];

/**
 * Converts a string to sentence case (first letter uppercase, rest lowercase)
 * Preserves known acronyms in uppercase
 * Examples:
 * - "HELLO WORLD" -> "Hello world"
 * - "hello world" -> "Hello world"
 * - "send SMS message" -> "Send SMS message"
 * - "export CSV file" -> "Export CSV file"
 * - "SMS" -> "SMS"
 * @param value - The string to convert
 * @returns The string in sentence case
 */
export const ensureHttps = (url: string): string => {
  if (!url || /^https:\/\//i.test(url)) {
    return url;
  }
  return `https://${url.replace(/^http:\/\//i, '')}`;
};

export const toSentenceCase = (value: string): string => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  // If it's a single word (no spaces), keep it as-is
  if (!value.includes(' ')) {
    return value;
  }

  // Split into words
  const words = value.split(/\s+/);

  const processedWords = words.map((word, index) => {
    const upperWord = word.toUpperCase();

    // Check if the word is a known acronym
    if (ACRONYMS.includes(upperWord)) {
      return upperWord;
    }

    // First word: capitalize first letter, lowercase rest
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }

    // Other words: all lowercase
    return word.toLowerCase();
  });

  return processedWords.join(' ');
};
